import { styled } from '@mui/material/styles';
import FuseMessage from '@fuse/core/FuseMessage';
import { memo, ReactNode, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { Layout1ConfigDefaultsType } from '@/components/theme-layouts/layout1/Layout1Config';
import { useFuseLayoutSettings } from '@fuse/core/FuseLayout/FuseLayout';
import FuseSuspense from '@fuse/core/FuseSuspense';
import { lazyRetry } from '@utils/lazyRetry';
import LeftSideLayout1 from './components/LeftSideLayout1';
import NavbarWrapperLayout1 from './components/NavbarWrapperLayout1';
import RightSideLayout1 from './components/RightSideLayout1';
import ToolbarLayout1 from './components/ToolbarLayout1';

// Create a fallback component
function ScrollContainer({ children }) {
  return <div className="relative flex flex-auto flex-col overflow-auto">{children}</div>;
}

function SafeScrollbars({ children }) {
  return (
    <Suspense fallback={<ScrollContainer>{children}</ScrollContainer>}>
      <FuseScrollbarsLazy>{children}</FuseScrollbarsLazy>
    </Suspense>
  );
}

const FuseScrollbarsLazy = lazyRetry(() => import('@fuse/core/FuseScrollbars'));
const FuseDialog = lazyRetry(() => import('@fuse/core/FuseDialog/FuseDialog'));

const Root = styled('div')(({ config }: { config: Layout1ConfigDefaultsType }) => ({
  ...(config.mode === 'boxed' && {
    clipPath: 'inset(0)',
    maxWidth: `${config.containerWidth}px`,
    margin: '0 auto',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
  }),
  ...(config.mode === 'container' && {
    '& .container': {
      maxWidth: `${config.containerWidth}px`,
      width: '100%',
      margin: '0 auto'
    }
  })
}));

type Layout1Props = {
  children?: ReactNode;
};

/**
 * The layout 1.
 */
function Layout1(props: Layout1Props) {
  const { children } = props;
  const settings = useFuseLayoutSettings();
  const config = settings.config as Layout1ConfigDefaultsType;

  return (
    <Root id="fuse-layout" config={config} className="flex flex-auto w-full">
      {config.leftSidePanel.display && <LeftSideLayout1 />}

      <div className="flex min-w-0 flex-auto">
        {config.navbar.display && config.navbar.position === 'left' && <NavbarWrapperLayout1 />}

        <main id="fuse-main" className="relative z-10 flex min-h-full min-w-0 flex-auto flex-col">
          {config.toolbar.display && (
            <ToolbarLayout1 className={config.toolbar.style === 'fixed' ? 'sticky top-0' : ''} />
          )}

          <div className="relative z-10 flex min-h-0 flex-auto flex-col">
            <Suspense>
              <FuseDialog />
            </Suspense>
            <Suspense>
              <SafeScrollbars>
                <FuseSuspense>
                  <Outlet />
                </FuseSuspense>
              </SafeScrollbars>
            </Suspense>
            {children}
          </div>
        </main>

        {config.navbar.display && config.navbar.position === 'right' && <NavbarWrapperLayout1 />}
      </div>

      {config.rightSidePanel.display && <RightSideLayout1 />}
      <FuseMessage />
    </Root>
  );
}

export default memo(Layout1);
